/**
 * The toast vocabulary every hand-rolled OverlayQA surface shares.
 *
 * Emily's call (board P0, 2026-09-14): ONE hand-rolled toast serves the
 * extension, the guest client review and the team-review drawer; the dashboard
 * keeps Sonner. The gate behind that call, run in real Chromium and recorded on
 * the board row: Sonner DOES render inside `overlayqa-overlay-host` but is
 * completely unstyled there, because `__insertCSS` hardcodes `document.head`
 * (sonner 2.0.7) and a shadow root does not inherit it. Injecting Sonner's own
 * stylesheet into the shadow root fixes that — but the guest bundle has ZERO
 * `import`/`require` across its 57 sections and builds by straight
 * concatenation, so it can never take a React package at all, and team review
 * inherits that limit because it asks the guest bundle for its toast over
 * `oq:toast`. One shared vanilla vocabulary reaches three surfaces; Sonner
 * could only ever reach two.
 *
 * Deliberately dependency-free and DOM-free: the guest bundle's copy is
 * GENERATED from this file, and a generator cannot inline anything that
 * imports.
 */
import { ACTIONABLE_TOAST_MIN_MS } from '../constants/toastHold.js';
/**
 * The four kinds, in severity order.
 *
 * `warning` is new here. Before it, a caller with a warning-shaped outcome had
 * to pick a neighbour, so one shared tone rendered as `info` on one surface and
 * `error` on another — the row's item (d).
 */
export const TOAST_KINDS = ['success', 'error', 'warning', 'info'];
/** Narrows unknown input at a surface boundary (an event detail, a bridge message). */
export function isToastKind(value) {
    return typeof value === 'string' && TOAST_KINDS.includes(value);
}
/** Passive outcome. Nothing to act on, so it gets out of the way. */
export const PASSIVE_TOAST_MS = 4000;
/**
 * Something the user has to READ before it goes: a refusal, or a warning.
 * Both carry information the interface does not otherwise show.
 */
export const READABLE_TOAST_MS = 7000;
/**
 * How long this toast stays on screen.
 *
 * The actionable floor is applied LAST and clamps upward only, so a call site
 * passing `duration: 4000` on an Undo cannot silently defeat it — that exact
 * shape is what `actionableToastHold.source.test.ts` exists to catch.
 */
export function toastDurationMs(kind, options = {}) {
    const base = options.durationMs
        ?? (kind === 'error' || kind === 'warning' ? READABLE_TOAST_MS : PASSIVE_TOAST_MS);
    return options.actionable ? Math.max(base, ACTIONABLE_TOAST_MIN_MS) : base;
}
/** The BEM block all three hand-rolled surfaces already ship. */
export function toastClassName(kind) {
    return `oq-toast oq-toast--${kind}`;
}
/**
 * The custom property the countdown bar reads.
 *
 * The bar is CSS-driven so it can pause with a single `animation-play-state`
 * rule, which is the only way it can track the timer rather than run a second
 * one beside it. A CSS animation cannot read a JS value, so the per-toast
 * window has to arrive as a variable on the element.
 */
export const TOAST_DURATION_VAR = '--oq-toast-duration';
//# sourceMappingURL=toastKind.js.map